// src/components/subscription/CustomCheckout.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

import { formatCurrency } from '../../utils/currency';
import axios from 'axios';
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 5000,
});

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CheckoutForm = ({
  clientSecret,
  plan,
  proratedAmount,
  isDowngrade,
  effectiveDate,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    try {
      const token = localStorage.getItem('ownerToken');

      const { error: paymentError, paymentIntent } =
        await stripe.confirmPayment({
          elements,
          redirect: 'if_required',
          confirmParams: {
            return_url: `${window.location.origin}/owner/membership`, // Update this URL
          },
        });

      //   if (paymentError) {
      //     setError(paymentError.message);
      //     navigate('/payment-failed', { state: { error: paymentError.message } });
      //   }
      if (paymentError) {
        setError(paymentError.message);
        // Instead of navigating, show error in the current page
        setProcessing(false);
        return;
      } else {
        const subscriptionData = {
          paymentIntentId: paymentIntent.id,
          planId: plan.id,
          isDowngrade,
          effectiveDate,
        };

        await api.post('/subscriptions/confirm', subscriptionData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        navigate('/owner/membership/payment-success', {
          state: {
            plan,
            isDowngrade,
            effectiveDate,
          },
        });
      }
    } catch (error) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className='space-y-6'>
      <PaymentElement />

      <div className='bg-gray-50 p-4 rounded-lg'>
        <h3 className='font-semibold mb-2'>Order Summary</h3>
        <div className='flex justify-between mb-2'>
          <span>{plan.name}</span>
          <span>{formatCurrency(plan.price / 100)}/month</span>
        </div>
        {proratedAmount && (
          <div className='flex justify-between text-sm text-gray-600'>
            <span>Prorated amount</span>
            <span>{formatCurrency(proratedAmount / 100)}</span>
          </div>
        )}
        <div className='border-t mt-2 pt-2 font-semibold flex justify-between'>
          <span>Total due today</span>
          <span>{formatCurrency((proratedAmount || plan.price) / 100)}</span>
        </div>
      </div>

      {error && <div className='text-red-500 text-sm'>{error}</div>}

      <button
        type='submit'
        disabled={!stripe || processing}
        className='w-full bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 disabled:opacity-50'
      >
        {processing ? 'Processing...' : 'Pay Now'}
      </button>
    </form>
  );
};

const CustomCheckout = () => {
  const location = useLocation();
  const [clientSecret, setClientSecret] = useState(null);
  const { plan, proratedAmount, isDowngrade, effectiveDate } =
    location.state || {};

  useEffect(() => {
    const createPaymentIntent = async () => {
      try {
        const token = localStorage.getItem('ownerToken');

        const response = await api.post(
          '/subscriptions/create-payment-intent',
          {
            planId: plan.id,
            proratedAmount,
            isDowngrade,
            effectiveDate,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setClientSecret(response.data.clientSecret);
      } catch (error) {
        console.error('Error creating payment intent:', error);
      }
    };

    if (plan) {
      createPaymentIntent();
    }
  }, [plan, proratedAmount, isDowngrade, effectiveDate]);

  if (!clientSecret) {
    return <div>Loading...</div>;
  }

  const options = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#3b82f6',
      },
    },
  };

  return (
    <div className='container mx-auto px-4 py-8 max-w-2xl'>
      <h1 className='text-2xl font-bold mb-6'>Complete Your Purchase</h1>
      <Elements stripe={stripePromise} options={options}>
        <CheckoutForm
          clientSecret={clientSecret}
          plan={plan}
          proratedAmount={proratedAmount}
          isDowngrade={isDowngrade}
          effectiveDate={effectiveDate}
        />
      </Elements>
    </div>
  );
};

export default CustomCheckout;
